import { useEffect } from 'react';
import catalog from './bangla-ui.json';
import { useSaasSession } from '../saas/SessionContext';

const textState = new WeakMap();
const attributeState = new WeakMap();
const excludedText = 'script, style, noscript, textarea, input, code, pre, [contenteditable="true"], [data-user-content], .mobile-pos__product, .mobile-pos__cart-items, .mobile-transaction-management__card, table td';
const excludedAttributes = 'script, style, noscript, [contenteditable="true"], [data-user-content], .mobile-pos__product, .mobile-pos__cart-items, .mobile-transaction-management__card, table td';
const translatedAttributes = ['placeholder', 'title', 'aria-label', 'alt'];

function preserveWhitespace(value, translated) {
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  const core = value.slice(leading.length, value.length - trailing.length || undefined);
  return `${leading}${translated}${trailing && core ? trailing : ''}`;
}

function isExcludedTextNode(node, root) {
  const parent = node.parentElement;
  return !parent || Boolean(parent.closest(excludedText)) || !root.contains(parent);
}

function translateTextNode(node, enabled) {
  const current = node.nodeValue;
  if (current == null) return;
  const state = textState.get(node);
  if (!enabled) {
    if (state && current === state.rendered) node.nodeValue = state.original;
    textState.delete(node);
    return;
  }
  const original = state && current === state.rendered ? state.original : current;
  const key = original.trim();
  if (!key || /[\u0980-\u09FF]/.test(key)) return;
  const translated = catalog[key];
  if (!translated || translated === key) return;
  const rendered = preserveWhitespace(original, translated);
  if (rendered !== current) node.nodeValue = rendered;
  textState.set(node, { original, rendered });
}

function translateAttribute(element, name, enabled) {
  const current = element.getAttribute(name);
  if (current == null) return;
  let states = attributeState.get(element);
  if (!enabled) {
    const state = states?.get(name);
    if (!state) return;
    if (state && current === state.rendered) element.setAttribute(name, state.original);
    states.delete(name);
    return;
  }
  if (!states) {
    states = new Map();
    attributeState.set(element, states);
  }
  const state = states.get(name);
  const original = state && current === state.rendered ? state.original : current;
  if (/^[\u0980-\u09FF]/.test(original.trim())) return;
  const translated = catalog[original.trim()];
  if (!translated || translated === original.trim()) return;
  if (translated !== current) element.setAttribute(name, translated);
  states.set(name, { original, rendered: translated });
}

function translateElement(element, enabled, root) {
  if (!(element instanceof Element) || !root.contains(element)) return;
  if (!element.closest(excludedAttributes)) {
    translatedAttributes.forEach((name) => translateAttribute(element, name, enabled));
    if (element.tagName === 'INPUT' && /^(button|submit|reset)$/i.test(element.type)) translateAttribute(element, 'value', enabled);
  }
  if (element.closest(excludedText)) return;
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) {
    if (!isExcludedTextNode(node, root)) translateTextNode(node, enabled);
  }
}

function restoreAll(root) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  let node;
  while ((node = walker.nextNode())) translateTextNode(node, false);
  root.querySelectorAll('*').forEach((element) => {
    translatedAttributes.forEach((name) => translateAttribute(element, name, false));
    if (element.tagName === 'INPUT' && /^(button|submit|reset)$/i.test(element.type)) translateAttribute(element, 'value', false);
  });
}

export default function BanglaUiBridge() {
  const { tenant } = useSaasSession();
  const enabled = tenant?.settings?.language === 'bn' || tenant?.settings?.Bangla_Language === true;

  useEffect(() => {
    const root = document.getElementById('root');
    if (!root || !enabled) return undefined;
    const html = document.documentElement;
    const previousLanguage = html.getAttribute('lang');
    html.setAttribute('lang', 'bn');

    const apply = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        if (!isExcludedTextNode(node, root)) translateTextNode(node, enabled);
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        translateElement(node, enabled, root);
      }
    };

    translateElement(root, true, root);
    const observer = new MutationObserver((records) => {
      records.forEach((record) => {
        if (record.type === 'characterData') apply(record.target);
        if (record.type === 'childList') record.addedNodes.forEach(apply);
        if (record.type === 'attributes' && record.target instanceof Element) {
          const name = record.attributeName;
          const isInputButtonValue = name === 'value' && record.target.tagName === 'INPUT' && /^(button|submit|reset)$/i.test(record.target.type);
          if ((translatedAttributes.includes(name) || isInputButtonValue) && !record.target.closest(excludedAttributes)) {
            translateAttribute(record.target, name, enabled);
          }
        }
      });
    });
    observer.observe(root, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: [...translatedAttributes, 'value'] });
    return () => {
      observer.disconnect();
      restoreAll(root);
      if (previousLanguage == null) html.removeAttribute('lang');
      else html.setAttribute('lang', previousLanguage);
    };
  }, [enabled]);

  return null;
}
